import type { LegacyRef } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import UIkit from 'uikit';
import assert from 'assert';
import InfosDecorator from './InfosDecorator';
import StateDecorator from '../../components/utils/state/StateDecorator';
import { useAppSelector } from '../../store/hooks';
import { isDataState } from '../../store/data/state';
import ReportModelService from '../../api/services/config/reportModel/ReportModelService';
import type {
  ApiReturnSite,
  ApiReturnSiteReportModel,
} from '@highimpact-frontend/api-types';
import { getSitesFromProject } from '../../store/config';
import type { ApiReturnModel } from '@highimpact-frontend/api-types';
import { getOrElse } from '../../utils/functions';
import ActionList from '../../components/utils/actionListComponent/ActionList';
import { useModal } from '../../utils/hooks/useModal';
import EditReportModels from '../../components/reportModels/EditReportModels';
import ManagementDecorator from './ManagementDecorator';

function ModelsDisplayer(
  _: object,
  ref: LegacyRef<HTMLDivElement>
): JSX.Element {
  const [siteModelsRecord, setSiteModelsRecord] = useState<
    Record<number, ApiReturnModel[]>
  >({});
  const [allModelsOfProject, setAllModelsOfProject] = useState<
    ApiReturnModel[]
  >([]);

  const projects = useAppSelector(
    (state) => state.data_slice.externals.projects
  );
  const sites = useAppSelector((state) => state.data_slice.externals.sites);
  const project_sites = useAppSelector(
    (state) => state.data_slice.externals.project_sites
  );

  const editModal = useModal<{ siteId: number }>();

  // Retrieve parameters from the URL.
  const { project_id, site_id, device_id } = useParams<{
    project_id: string | undefined;
    site_id: string | undefined;
    device_id?: string | undefined;
  }>();

  const params = {
    project_id: project_id ? Number.parseInt(project_id) : null,
    site_id: site_id ? Number.parseInt(site_id) : null,
    device_id: device_id ? Number.parseInt(device_id) : null,
  };

  const handleSelection = (values: number[], site_id: number) => {
    setSiteModelsRecord((prev) => ({
      ...prev,
      [site_id]: allModelsOfProject.filter((m) => values.includes(m.id)),
    }));

    try {
      ReportModelService.processSiteReportModelAssociation(site_id, values);
      editModal.close();
      UIkit.notification({
        message: "L'association a été faite avec succès",
        status: 'success',
      });
    } catch (e) {
      console.error('Une erreur ses produite', e);
      UIkit.notification({
        message: "L'association n'a pas été faite",
        status: 'danger',
      });
    }
  };

  useEffect(() => {
    ReportModelService.getSiteReportModelByProject(params.project_id!).then(
      (response: ApiReturnSiteReportModel[]) => {
        const initialRecord = response.reduce((acc, curr) => {
          acc[curr.site_id] = curr.report_models;
          return acc;
        }, {} as Record<number, ApiReturnModel[]>);

        setSiteModelsRecord(initialRecord);
      }
    );

    ReportModelService.getProjectReportModelByProject(params.project_id!).then(
      (response) => setAllModelsOfProject(response)
    );
  }, []);

  return ManagementDecorator(ref, () => (
    <InfosDecorator>
      {StateDecorator([projects, project_sites, sites], ref, () => {
        assert(isDataState(projects));
        assert(isDataState(project_sites));
        assert(isDataState(sites));

        const sitesFromProject = getSitesFromProject(
          Object.values(projects.data).find(
            (project) => project.id === params.project_id
          )!,
          project_sites,
          sites
        );

        const headings = ['Site', 'Modèles'] as const;

        type RowKey = (typeof headings)[number];
        type Row = Record<RowKey, string> & { site: ApiReturnSite };

        const createRow = (site: ApiReturnSite): Row => {
          return {
            Site: site.name,
            Modèles: getOrElse(
              siteModelsRecord[site.id] &&
                siteModelsRecord[site.id]!.map((md) => md.name).join(', ')
            ),
            site,
          };
        };

        const rows = sitesFromProject.map((st) => createRow(st));

        return (
          <div className="models-list">
            <table className="uk-table uk-table-divider">
              <thead>
                <tr>
                  {headings.map((head, index) => (
                    <th key={index}>{head}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    {Object.entries(row).map(
                      ([key, value]) =>
                        typeof value === 'string' && <td key={key}>{value}</td>
                    )}
                    <td>
                      <ActionList
                        actions={[
                          {
                            name: 'update',
                            onclick: () =>
                              editModal.open({ siteId: row.site.id }),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {editModal.isOpen && (
        <EditReportModels
          defaultValues={siteModelsRecord[editModal.params?.siteId!]!.map(
            (model) => model.id
          )}
          options={allModelsOfProject}
          onOptionClick={(values) =>
            handleSelection(values, editModal.params?.siteId!)
          }
          open={editModal.isOpen}
          onCancel={editModal.close}
        />
      )}
    </InfosDecorator>
  ));
}

export default forwardRef(ModelsDisplayer);
